import React from "react";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { ReactComponent as LinkedinIcon } from "../../assets/img/profile/linkedin-icon.svg";
import { HiOutlineMail, HiOutlineGlobe, HiOutlineOfficeBuilding, HiOutlineInformationCircle } from "react-icons/hi";
import { FaLinkedinIn, FaXTwitter } from "react-icons/fa6";
import { BiTrash } from "react-icons/bi";

const SocialLink = ({ icon: Icon, url, label }) => {
  if (!url) return null;
  return (
    <a
      href={url.startsWith('http') ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group"
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-[#7e7cff]/10 flex items-center justify-center group-hover:from-primary group-hover:to-[#7e7cff] transition-all duration-300">
        <Icon className="w-5 h-5 text-primary group-hover:text-white transition-colors" />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <span className="text-sm font-medium text-secondary truncate">{url}</span>
      </div>
    </a>
  );
};

const SectionCard = ({ title, subtitle, children, icon: Icon }) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="p-6 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-secondary abel-font">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

const ViewOrgProfileLayout = ({
  userProfileData,
  handleSendVerifyEmail,
  sendEmailLoading,
  userId,
  onDeleteAccount,
}) => {
  const hasSocialLinks = userProfileData?.linkedIn || userProfileData?.x || userProfileData?.email;
  const hasWebsites = userProfileData?.websites?.length > 0;

  return (
    <div className="w-full flex flex-col gap-6 sm:gap-8">
      {/* Organization Description Section */}
      {userProfileData?.description && (
        <SectionCard title="About Organization" icon={HiOutlineOfficeBuilding}>
          <MarkdownPreview
            source={userProfileData?.description}
            style={{ background: "transparent" }}
            wrapperElement={{
              "data-color-mode": "light",
            }}
            className="prose prose-sm sm:prose max-w-none"
          />
        </SectionCard>
      )}

      {/* Contact & Social Section */}
      {(hasSocialLinks || hasWebsites) && (
        <SectionCard title="Contact Information" subtitle="Get in touch with us" icon={HiOutlineGlobe}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SocialLink icon={FaLinkedinIn} url={userProfileData?.linkedIn} label="LinkedIn" />
            <SocialLink icon={FaXTwitter} url={userProfileData?.x} label="X (Twitter)" />
            
            {/* Email - only show for own profile */}
            {!userId && userProfileData?.email && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-[#7e7cff]/10 flex items-center justify-center">
                  <HiOutlineMail className="w-5 h-5 text-primary" />
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs text-gray-500 font-medium">Email</span>
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${userProfileData?.email}`} className="text-sm font-medium text-secondary truncate hover:text-primary transition-colors">
                      {userProfileData?.email}
                    </a>
                    {userProfileData?.email_verified ? (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-600 rounded-full">Verified</span>
                    ) : (
                      <button
                        onClick={handleSendVerifyEmail}
                        disabled={sendEmailLoading}
                        className="px-2 py-0.5 text-[10px] font-bold bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
                      >
                        {sendEmailLoading ? "Sending..." : "Verify"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Verified badge for public profiles */}
            {userId && userProfileData?.email_verified && (
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-xs text-gray-500 font-medium">Status</span>
                  <span className="text-sm font-medium text-emerald-600">Verified Organization</span>
                </div>
              </div>
            )}
            
            {/* Websites */}
            {userProfileData?.websites?.map((website, index) => (
              <SocialLink key={index} icon={HiOutlineGlobe} url={website} label={`Website ${index + 1}`} />
            ))}
          </div>
          
          {!userId && !userProfileData?.email_verified && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-xl shadow-sm">
              <p className="text-sm text-yellow-700">
                ⚠️ Please verify your email address to access all FunDeSci features.
              </p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Danger Zone - Only show on own profile */}
      {!userId && onDeleteAccount && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border-2 border-red-100">
          <div className="p-6 bg-gradient-to-r from-red-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <BiTrash className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-red-600 abel-font">Danger Zone</h3>
                <p className="text-sm text-gray-500">Permanent account deletion</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-red-50 rounded-xl">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Delete Your Account</h4>
                <p className="text-sm text-gray-600">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
              </div>
              <button
                onClick={onDeleteAccount}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all duration-300 hover:shadow-lg flex items-center gap-2 whitespace-nowrap"
              >
                <BiTrash size={18} />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewOrgProfileLayout;
